/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { Formik } from 'formik';
import * as yup from 'yup';
import styled from 'styled-components';

import AutocompleteComponent from '../../../../components/common/forms/Autcomplete/AutocompleteComponent';
import { API_URL, ROLES } from '../../../../utils/constants';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../../components/common/forms/TextInput/TextInputComponent';
import PackageRow from './PackageRow';
import ProductsApi from '../../../../services/api/ProductsApi';
import PackagedProductsApi from '../../../../services/api/PackagedProductsApi';
import SupplierProductsApi from '../../../../services/api/SupplierProductsApi';
import UserLocalService from '~/src/services/logic/userService';
import IngredientsApi from '~/src/services/api/IngredientsApi';
import { sortPackaging } from '../../../../utils/misc';
import SupplierProductDetails from './SupplierProductDetails';
import CheckboxInputComponent from '../../../../components/common/forms/CheckboxInput/CheckboxInputComponent';
import ButtonComponent from '../../../../components/common/forms/Button/ButtonComponent';
import IngredientBox from '~/src/scenes/nutrition/components/IngredientBox';
import { IngredientGetDto } from '~/src/types/ingredientGetDto';
import RolesDependent from '~/src/components/common/RolesDependent';
import SecondaryButton from '~/src/components/common/forms/Button/SecondaryButton';

import mergeIcon from '~/src/static/img/merge.svg';
import { Colors } from '~/src/scss/common/style';
import TableWrapper from '~/src/components/common/forms/TableWrapper/TableWrapper';
import BorderlessButton from '~/src/components/common/forms/Button/BorderlessButton';

import xIcon from '~/src/static/img/xIcon.svg';
import PrimaryButton from '~/src/components/common/forms/Button/PrimaryButton';

const validationSchema = yup.object().shape({
  sku: yup.string().label('SKU'),
  manufacturerName: yup.string().label('Manufacturer Name'),
  minimumGuaranteedDays: yup.number(),
  expirationDays: yup.number(),
  variant: yup.string(),
  palletId: yup.string().required(),
  allowXRotation: yup.bool(),
  allowYRotation: yup.bool(),
  allowZRotation: yup.bool(),
  servingSize: yup.number(),
  milkFat: yup.number(),
  milkProtein: yup.number(),
  starchGlucose: yup.number(),
  sucroseInvertSugarIsoglucose: yup.number(),
});

export default function ProductDetails({ product, mergeFunc }): JSX.Element {
  const [activeKey, setActiveKey] = useState('home');
  const [initialPackages, setInitialPackages] = useState(null);
  const [packagedProducts, setPackagedProducts] = useState(null);
  const [supplierProducts, setSupplierProducts] = useState(product.supplierProducts);
  const [tabKey, setTabKey] = useState('');
  const [tabKeySupplier, setTabKeySupplier] = useState('');
  const [ingredient, setIngredient] = useState<IngredientGetDto | null>(null);

  const history = useHistory();

  useEffect(() => {
    if (product.packagedProducts && product.packagedProducts.length > 0) {
      const packages = product.packagedProducts?.map((item) => item.pack);
      const sortedPackageList = sortPackaging(product.packagedProducts);
      setInitialPackages(packages);
      setPackagedProducts(sortedPackageList);
    } else {
      setInitialPackages([]);
    }
    if (product.supplierProducts && product.supplierProducts.length > 0) {
      setTabKeySupplier(product.supplierProducts[0].supplier.id);
    }
    getProductIngredient();
  }, []);

  const getProductIngredient = async () => {
    const res = await IngredientsApi.getOrCreateForProduct(product.id);
    if (res.status === StatusCodes.OK) {
      setIngredient(res.data);
    }
  };

  const updateProduct = async (values) => {
    const res = await ProductsApi.update(product.id, values);
  };

  const handleSelectChange = async (e, v) => {
    const res = await PackagedProductsApi.create(product.id, v);
    if (res.status === StatusCodes.OK) {
      const sortedPackageList = sortPackaging(res.data);
      setPackagedProducts(sortedPackageList);
      setTabKey(v.name);
    }
  };

  const deleteUniqueProductPackage = async (id) => {
    const res = await PackagedProductsApi.delete(id);
    if (res.status === StatusCodes.OK) {
      var newProductPackages = packagedProducts.filter((item) => item.id !== id);
      setPackagedProducts(newProductPackages);
    }
  };

  const handleSupplierSelect = async (e, v) => {
    if (v !== '') {
      const res = await SupplierProductsApi.create({ supplierId: v, productId: product.id });
      if (res.status === StatusCodes.OK) {
        setSupplierProducts([...supplierProducts, res.data]);
        setTabKeySupplier(v.id);
      }
    }
  };

  const resetLayout = async (id: string): Promise<void> => {
    const res = await ProductsApi.resetLayout(id);
    if (res.status === StatusCodes.OK) {
      // console.log('layout reset');
    }
  };

  const handleSupplierDelete = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const res = await SupplierProductsApi.delete(id);
    const newSupplierProducts = supplierProducts.filter((x) => x.id !== id);
    setSupplierProducts(newSupplierProducts);
  };
  return (
    <Wrapper>
      <Formik
        initialValues={{
          sku: product.sku || '',
          manufacturerName: product.manufacturerName || '',
          minimumGuaranteedDays: product.minimumGuaranteedDays || '',
          expirationDays: product.expirationDays || '',
          variant: product.variant || '',
          servingSize: product.servingSize || '',
          milkFat: product.milkFat || '',
          milkProtein: product.milkProtein || '',
          starchGlucose: product.starchGlucose || '',
          sucroseInvertSugarIsoglucose: product.sucroseInvertSugarIsoglucose || '',
          boxCanCarry: product.boxCanCarry || '',
          allowXRotation: product.allowXRotation || false,
          allowYRotation: product.allowYRotation || false,
          allowZRotation: product.allowZRotation || false,
          palletId: product.palletId || '',
        }}
        validationSchema={validationSchema}
      >
        {({ values, handleChange, isSubmitting, setFieldValue, errors }) => (
          <form autoComplete="off">
            <div style={{ padding: '20px' }}>
              <RolesDependent roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}>
                {product.id}{' '}
                <SecondaryButton
                  icon={mergeIcon}
                  onClick={() => {
                    if (
                      window.confirm(`Please review the following: \n
                    - Products in Orders will be transfered\n
                    - Warehouse Transactions will be transfered\n
                    - Packaging Data will be DELETED\n
                    - Supplier Data will be DELETED\n
                    - Ingredients and Nutrition calculations will be DELETED`) === true
                    ) {
                      mergeFunc(product.id);
                    }
                  }}
                  title="Merge products to this one"
                />
              </RolesDependent>
            </div>
            {ingredient && (
              <IngredientBox
                ingredient={ingredient}
                key={ingredient.id}
                onClick={() => history.push(`/nutrition/${product.id}`)}
              />
            )}
            <Title>Passport Data</Title>
            <FormWrapper>
              <FormRow>
                <TextInputComponent
                  name="manufacturerName"
                  label="ManufacturerName"
                  value={values.manufacturerName}
                  onBlur={(e) => {
                    updateProduct(values);
                  }}
                  onChange={handleChange}
                  cols={4}
                />
                <AutocompleteComponent
                  url={`${API_URL}/products/pallets/get`}
                  name="palletId"
                  label="Pallet"
                  initialValue={product.palletId}
                  onChange={async (e, v) => {
                    setFieldValue('palletId', v);
                    const newValues = { ...values, palletId: v }; // for some reason, set field value is not immediate
                    updateProduct(newValues);
                  }}
                  getOptionLabel={(option) => `${option.name}`}
                  cols={4}
                  errorMessage={errors.palletId}
                />
                <AutocompleteComponent
                  url={`${API_URL}/storage-conditions/get`}
                  name="storageConditionId"
                  label="Storage Condition"
                  initialValue={product.storageConditionId}
                  onChange={async (e, v) => {
                    setFieldValue('storageConditionId', v);
                    const newValues = { ...values, storageConditionId: v || null }; // for some reason, set field value is not immediate
                    updateProduct(newValues);
                  }}
                  getOptionLabel={(option) => `${option.conditionDescription}`}
                  cols={4}
                  errorMessage={errors.storageConditionId}
                />
              </FormRow>
              <FormRow>
                <div style={{ width: '20%', padding: '0 5px' }}>
                  <TextInputComponent
                    name="sku"
                    label="Sku"
                    value={values.sku}
                    onBlur={(e) => {
                      updateProduct(values);
                    }}
                    onChange={handleChange}
                    cols={12}
                  />
                </div>
                <div style={{ width: '20%', padding: '0 5px' }}>
                  <TextInputComponent
                    name="minimumGuaranteedDays"
                    label="Minimum guaranteed days"
                    value={values.minimumGuaranteedDays}
                    onBlur={(e) => {
                      updateProduct(values);
                    }}
                    onChange={handleChange}
                    type="number"
                    cols={12}
                  />
                </div>
                <div style={{ width: '20%', padding: '0 5px' }}>
                  <TextInputComponent
                    label="Expiration days"
                    name="expirationDays"
                    value={values.expirationDays}
                    onBlur={(e) => {
                      updateProduct(values);
                    }}
                    onChange={handleChange}
                    type="number"
                    cols={12}
                  />
                </div>
                <div style={{ width: '20%', padding: '0 5px' }}>
                  <TextInputComponent
                    label="Variant"
                    name="variant"
                    value={values.variant}
                    onBlur={(e) => {
                      updateProduct(values);
                    }}
                    onChange={handleChange}
                    type="number"
                    cols={12}
                  />
                </div>
                <div style={{ width: '20%', padding: '0 5px' }}>
                  <TextInputComponent
                    label="Box can cary (kg)"
                    name="boxCanCarry"
                    value={values.boxCanCarry}
                    onBlur={(e) => {
                      updateProduct(values);
                    }}
                    onChange={handleChange}
                    type="number"
                    cols={12}
                  />
                </div>
              </FormRow>
              <FormRow>
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <div style={{ width: '20%', padding: '0 5px' }}>
                    <CheckboxInputComponent
                      id="allowXRotation"
                      name="allowXRotation"
                      label="Allow X Rotation"
                      checked={values.allowXRotation}
                      onChange={(e) => {
                        setFieldValue('allowXRotation', e.target.checked);
                        updateProduct({ ...values, allowXRotation: e.target.checked });
                      }}
                      cols={12}
                    />
                  </div>
                  <div style={{ width: '20%', padding: '0 5px' }}>
                    <CheckboxInputComponent
                      id="allowYRotation"
                      name="allowYRotation"
                      label="Allow Y Rotation"
                      checked={values.allowYRotation}
                      onChange={(e, v) => {
                        setFieldValue('allowYRotation', e.target.checked);
                        updateProduct({ ...values, allowYRotation: e.target.checked });
                      }}
                      cols={12}
                    />
                  </div>
                  <div style={{ width: '20%', padding: '0 5px' }}>
                    <CheckboxInputComponent
                      id="allowZRotation"
                      name="allowZRotation"
                      label="Allow Z Rotation"
                      checked={values.allowZRotation}
                      onChange={(e) => {
                        setFieldValue('allowZRotation', e.target.checked);
                        updateProduct({ ...values, allowZRotation: e.target.checked });
                      }}
                      cols={12}
                    />
                  </div>
                </div>
              </FormRow>
              <FormRow>
                <div style={{ width: '20%', padding: '0 5px' }}>
                  <TextInputComponent
                    name="servingSize"
                    label="servingSize"
                    value={values.servingSize}
                    onBlur={(e) => {
                      updateProduct(values);
                    }}
                    onChange={handleChange}
                    type="number"
                    cols={12}
                  />
                </div>
                <div style={{ width: '20%', padding: '0 5px' }}>
                  <TextInputComponent
                    name="milkFat"
                    label="Milk fat"
                    value={values.milkFat}
                    onBlur={(e) => {
                      updateProduct(values);
                    }}
                    onChange={handleChange}
                    type="number"
                    cols={12}
                  />
                </div>
                <div style={{ width: '20%', padding: '0 5px' }}>
                  <TextInputComponent
                    name="milkProtein"
                    label="Milk protein"
                    value={values.milkProtein}
                    onBlur={(e) => {
                      updateProduct(values);
                    }}
                    onChange={handleChange}
                    type="number"
                    cols={12}
                  />
                </div>
                <div style={{ width: '20%', padding: '0 5px' }}>
                  <TextInputComponent
                    name="starchGlucose"
                    label="Starch glucose"
                    value={values.starchGlucose}
                    onBlur={(e) => {
                      updateProduct(values);
                    }}
                    onChange={handleChange}
                    type="number"
                    cols={12}
                  />
                </div>
                <div style={{ width: '20%', padding: '0 5px' }}>
                  <TextInputComponent
                    name="sucroseInvertSugarIsoglucose"
                    label="Sucrose invert sugar isoglucose"
                    value={values.sucroseInvertSugarIsoglucose}
                    onBlur={(e) => {
                      updateProduct(values);
                    }}
                    onChange={handleChange}
                    type="number"
                    cols={12}
                  />
                </div>
              </FormRow>
            </FormWrapper>
          </form>
        )}
      </Formik>

      <Title>Packaging</Title>
      {/* <FormRow title="Packaging" /> */}
      <PrimaryButton title="reset layout" onClick={() => resetLayout(product.id)} />
      <BoxWrapper>
        {initialPackages !== null ? (
          <AutocompleteComponent
            url={`${API_URL}/products/packs/get-without-cu-du`}
            name="packages"
            label="Add Packaging"
            initialValue={initialPackages}
            onChange={handleSelectChange}
            getOptionLabel={(option) => `${option.name}`}
          />
        ) : null}
      </BoxWrapper>
      <Table style={{ display: 'table', padding: '15px' }}>
        <thead>
          <tr>
            <StyledTd></StyledTd>
            <StyledTd>Contains</StyledTd>
            <StyledTd>of package</StyledTd>
            <StyledTd>Width (cm)</StyledTd>
            <StyledTd>Lenght (cm)</StyledTd>
            <StyledTd>Hight (cm)</StyledTd>
            <StyledTd>Net weight (g)</StyledTd>
            <StyledTd>Gross weight (g)</StyledTd>
            <StyledTd>Max label width (cm)</StyledTd>
            <StyledTd>Max label hight (cm)</StyledTd>
            <StyledTd>Gtin</StyledTd>
            <StyledTd>Weight Capacity (g)</StyledTd>
            <StyledTd></StyledTd>
          </tr>
        </thead>
        <StyleTBody>
          {packagedProducts !== null && tabKey !== null
            ? packagedProducts.map((packagedProduct) => {
                return (
                  // <tr>
                  <PackageRow
                    packagedProduct={packagedProduct}
                    key={packagedProduct.id}
                    onDelete={deleteUniqueProductPackage}
                  />
                  // </tr>
                );
              })
            : null}
        </StyleTBody>
      </Table>
      <Title>Suppliers</Title>
      <BoxWrapper>
        <AutocompleteComponent
          url={`${API_URL}/contractors/suppliers/get`}
          name="suppliers"
          label="Add Supplier"
          initialValue={[]}
          onChange={handleSupplierSelect}
          getOptionLabel={(option) => `${option.name}`}
        />
      </BoxWrapper>
      {supplierProducts.length > 0 && (
        <StyledTabs
          activeKey={tabKeySupplier}
          onSelect={(k) => {
            setTabKeySupplier(k);
          }}
        >
          {supplierProducts.map((supplierProduct) => {
            return (
              <StyledTab
                eventKey={supplierProduct.supplier.id}
                title={
                  <StyledTitleTab>
                    {supplierProduct.supplier.name}{' '}
                    <BorderlessButton
                      onClick={(e) => handleSupplierDelete(e, supplierProduct.id)}
                      iconSize={20}
                      icon={xIcon}
                    />
                  </StyledTitleTab>
                }
                key={supplierProduct.supplier.id}
              >
                <SupplierProductDetails supplierProduct={supplierProduct} />
              </StyledTab>
            );
          })}
        </StyledTabs>
      )}
    </Wrapper>
  );
}

const StyleTBody = styled.tbody`
  margin-top: 10px;
  tr td:first-child {
    border: none;
  }
  tr td:first-child {
    border: none;
  }

  tr:first-child {
    td:nth-child(2) {
      border-top-left-radius: 5px;
    }
    td:last-child {
      border-top-right-radius: 5px;
    }
  }
  tr:last-child {
    td:nth-child(2) {
      border-bottom-left-radius: 5px;
    }
    td:last-child {
      border-bottom-right-radius: 5px;
    }
  }
`;

const StyledTitleTab = styled.div``;

const StyledTabs = styled(Tabs)`
  .nav-link {
    color: #929292;
    font-size: 15px;
    font-weight: 700;
    background: #e7e7e7;
    margin-right: 5px;
    border: 1px solid transparent;
    div {
      display: flex;
    }
  }
  .nav-link.active {
    display: flex;
    color: ${Colors.darkBrown};
    font-size: 15px;
    font-weight: 700;
  }
`;

const StyledTab = styled(Tab)`
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  background: #e7e7e7;
`;

const Wrapper = styled.div`
  background: #f4f4f4;
  position: relative;

  &:after {
    position: absolute;
    left: -50px;
    right: -50px;
    top: 0;
    right: 0;
    backgorund: red;
  }
`;

const StyledTd = styled.td`
  color: #642b26;
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
`;

const FormWrapper = styled.div`
  padding: 75px 105px;
  border-radius: 15px;
  background: #fff;
  margin: 15px;
`;

const Title = styled.h2`
  font-weight: bold;
  font-size: 20px;
  color: #343434;
  color: ${Colors.darkBrown};
  text-align: center;
  width: 100%;
  margin: 60px 0 40px 0;
`;

const BoxWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  margin-bottom: 25px;
  padding: 21px;
  width: 100%;
  background: rgba(239, 125, 0, 0.09);
  border: 2px solid ${Colors.newOrage};
  border-radius: 10px;
`;

const Table = styled.div``;
